import React, { useEffect, useId, useRef, useState } from "react";
import {
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

/**
 * Toolbar unificada: búsqueda + popover Filtrar + chips activos + acciones.
 */
export default function AdminFilterToolbar({
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Buscar…",
  searchAriaLabel = "Buscar",
  filterTitle = "Filtrar",
  filterCount = 0,
  onClearAll,
  chips = [],
  actions = null,
  children,
}) {
  const panelId = useId();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) {
      if (wrapRef.current?.contains(e.target)) return;
      // PortalSelect menus render in document.body
      if (e.target.closest?.("[data-portal-select-menu]")) return;
      setOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const hasChips = chips.length > 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-brand-lavender/25 bg-white px-3 h-11 shadow-sm">
            <MagnifyingGlassIcon
              className="h-4 w-4 shrink-0 text-brand-gray"
              aria-hidden
            />
            <input
              type="search"
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") onSearchChange?.("");
              }}
              placeholder={searchPlaceholder}
              aria-label={searchAriaLabel}
              className="portal-value min-w-0 flex-1 border-0 bg-transparent placeholder:text-brand-gray/80 focus:outline-none"
            />
            {searchValue ? (
              <button
                type="button"
                onClick={() => onSearchChange?.("")}
                className="rounded-full p-1 text-brand-gray hover:bg-brand-lilac/40 hover:text-brand-dark"
                aria-label="Limpiar búsqueda"
              >
                <XMarkIcon className="h-4 w-4" aria-hidden />
              </button>
            ) : null}
          </div>

          {children ? (
            <div className="relative shrink-0" ref={wrapRef}>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-controls={panelId}
                className={[
                  "inline-flex h-11 items-center gap-2 rounded-xl border px-3.5 font-semibold transition-colors",
                  filterCount > 0 || open
                    ? "border-brand-lavender bg-brand-lilac/40 text-brand-navy"
                    : "border-brand-lavender/25 bg-white text-brand-navy hover:bg-brand-lilac/25",
                ].join(" ")}
              >
                <FunnelIcon className="h-4 w-4 shrink-0" aria-hidden />
                <span className="text-[0.9375rem]">{filterTitle}</span>
                {filterCount > 0 ? (
                  <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-md bg-brand-navy px-1.5 text-[0.75rem] font-bold text-white tabular-nums">
                    {filterCount}
                  </span>
                ) : null}
              </button>

              {open ? (
                <div
                  id={panelId}
                  role="dialog"
                  aria-label={filterTitle}
                className="absolute right-0 z-[90] mt-2 w-[min(100vw-2rem,22rem)] max-h-[min(70vh,32rem)] overflow-y-auto rounded-xl border border-brand-lavender/25 bg-white p-4 shadow-lg shadow-brand-navy/10"
                >
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <p className="portal-section-title !text-[1rem]">Filtros</p>
                    {filterCount > 0 && onClearAll ? (
                      <button
                        type="button"
                        onClick={() => {
                          onClearAll();
                        }}
                        className="portal-meta font-medium text-brand-lavender hover:underline"
                      >
                        Limpiar
                      </button>
                    ) : null}
                  </div>
                  <div className="space-y-3">{children}</div>
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="inline-flex h-9 items-center rounded-lg bg-brand-navy px-3.5 text-[0.875rem] font-semibold text-white hover:brightness-110"
                    >
                      Listo
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {actions ? <div className="shrink-0">{actions}</div> : null}
      </div>

      {hasChips ? (
        <div className="flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              onClick={chip.onRemove}
              className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-brand-lavender/30 bg-brand-lilac/35 py-1.5 pl-2.5 pr-2 text-left transition-colors hover:bg-brand-lilac/55"
              aria-label={`Quitar filtro ${chip.label}`}
            >
              <span className="portal-meta !text-brand-navy truncate">
                <span className="font-semibold">{chip.field}: </span>
                {chip.value}
              </span>
              <XMarkIcon className="h-3.5 w-3.5 shrink-0 text-brand-navy/70" aria-hidden />
            </button>
          ))}
          {onClearAll ? (
            <button
              type="button"
              onClick={onClearAll}
              className="portal-meta font-medium text-brand-lavender hover:underline px-1"
            >
              Limpiar todo
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function AdminFilterField({ label, children }) {
  return (
    <div>
      <label className="portal-label block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
